/**
 * useRoomSync — drop-in replacement for the legacy `setInterval(fetch, 2000)`
 * polling loop. Uses Server-Sent Events for near-realtime updates, with an
 * automatic fallback to interval polling if EventSource fails or isn't
 * supported.
 *
 *   const { data: room, refresh, status } = useRoomSync({
 *     code,
 *     endpoint: "rooms",          // or "group-rooms"
 *     fetchFn: () => api.getRoom(code),
 *     onError: (err) => { ... },
 *   });
 *
 * - `data` is the latest room snapshot (null until first message).
 * - `refresh()` triggers an explicit HTTP GET (useful right after a mutation
 *   when we want to nudge state without waiting for the server tick).
 * - `status` is one of "connecting" | "open" | "polling" | "closed".
 *
 * The SSE endpoint pushes a new snapshot every time the room doc changes
 * (server-side poll @ 500ms). When the room disappears, it emits a
 * `not_found` event and we surface that to the caller via `onError`.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { API } from "./api";

const POLL_INTERVAL_MS = 2000;

export function useRoomSync({ code, endpoint = "rooms", fetchFn, onError }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("connecting");
  const esRef = useRef(null);
  const pollRef = useRef(null);
  const onErrorRef = useRef(onError);
  const fetchFnRef = useRef(fetchFn);

  // Keep latest callbacks without re-subscribing
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { fetchFnRef.current = fetchFn; }, [fetchFn]);

  const refresh = useCallback(async () => {
    try {
      const r = await fetchFnRef.current();
      setData(r);
      return r;
    } catch (e) {
      onErrorRef.current?.(e);
      return null;
    }
  }, []);

  // Polling fallback — used when SSE isn't available or has dropped.
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    setStatus("polling");
    refresh();
    pollRef.current = setInterval(refresh, POLL_INTERVAL_MS);
  }, [refresh]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!code) return undefined;
    setStatus("connecting");

    if (typeof window === "undefined" || typeof window.EventSource === "undefined") {
      startPolling();
      return () => stopPolling();
    }

    const url = `${API}/${endpoint}/${code}/events`;
    let cancelled = false;
    const es = new window.EventSource(url, { withCredentials: false });
    esRef.current = es;

    es.onopen = () => {
      if (cancelled) return;
      stopPolling();
      setStatus("open");
    };

    es.onmessage = (ev) => {
      if (cancelled) return;
      try {
        const snap = JSON.parse(ev.data);
        setData(snap);
      } catch {
        /* malformed message — ignore */
      }
    };

    es.addEventListener("not_found", () => {
      if (cancelled) return;
      onErrorRef.current?.({ response: { status: 404 } });
      es.close();
      setStatus("closed");
    });

    es.onerror = () => {
      if (cancelled) return;
      // EventSource auto-reconnects, but in case of a hard failure (CORS,
      // 5xx, proxy-strip) we also kick polling as a safety net so the UI
      // stays fresh while the browser keeps retrying SSE in the background.
      startPolling();
    };

    return () => {
      cancelled = true;
      stopPolling();
      try { es.close(); } catch { /* noop */ }
      esRef.current = null;
    };
  }, [code, endpoint, startPolling, stopPolling]);

  return { data, setData, refresh, status };
}
